const postsToRefresh = new Set();
const pendingRequests = new Map();

// Each resource describes what needs refreshing. Navigation, requests and
// applying streams to the incoming cached body are shared below.
const refreshResources = [
  {
    selector: '[data-cache-refresh-post-number]',
    skipSelector: '.show-post, .show-post-modal',
    trackVisit(url) {
      const match = /^\/posts\/([^/]+)$/.exec(new URL(url, globalThis.location.origin).pathname);
      if (match) postsToRefresh.add(decodeURIComponent(match[1]));
    },
    requests() {
      return Array.from(postsToRefresh, number => ({
        url: `/posts/${encodeURIComponent(number)}/refresh_feed_card`,
        onApplied: () => postsToRefresh.delete(number)
      }));
    },
    async beforeRefresh() {
      const { flushPollSubmissions } = await import('../controllers/user_controller');
      await flushPollSubmissions();
    }
  },
  {
    selector: '[data-cache-refresh-showcase-number]',
    requests(elements) {
      const numbers = [...new Set(elements.map(element => element.dataset.cacheRefreshShowcaseNumber))];
      const requests = [];
      for (let offset = 0; offset < numbers.length; offset += 200) {
        const params = new URLSearchParams();
        numbers.slice(offset, offset + 200).forEach(number => params.append('showcase_numbers[]', number));
        requests.push({ url: `/showcases/refresh_saved_state?${params}` });
      }
      return requests;
    }
  }
];

document.addEventListener('turbo:before-visit', event => {
  refreshResources.forEach(resource => resource.trackVisit?.(event.detail.url));
});

document.addEventListener('turbo:before-render', event => {
  const body = event.detail.newBody;
  const jobs = refreshResources.flatMap(resource => {
    if (resource.skipSelector && body.querySelector(resource.skipSelector)) return [];
    const elements = Array.from(body.querySelectorAll(resource.selector));
    if (elements.length === 0) return [];
    const requests = resource.requests(elements);
    return requests.length ? [{ resource, requests }] : [];
  });
  if (jobs.length === 0) return;

  event.preventDefault();
  Promise.allSettled(jobs.map(async ({ resource, requests }) => {
    await resource.beforeRefresh?.();
    await Promise.allSettled(requests.map(async request => {
      try {
        const stream = await fetchRefreshStream(request.url);
        if (stream && applyRefreshStream(stream, body)) request.onApplied?.();
      } catch (error) {
        console.error('Unable to refresh cached content:', error);
      }
    }));
  })).finally(() => event.detail.resume());
});

function fetchRefreshStream(url) {
  if (!pendingRequests.has(url)) {
    const request = (async () => {
      const response = await fetch(url, {
        headers: { Accept: 'text/vnd.turbo-stream.html', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin', cache: 'no-store'
      });
      return response.ok && !response.redirected ? response.text() : null;
    })().finally(() => pendingRequests.delete(url));
    pendingRequests.set(url, request);
  }
  return pendingRequests.get(url);
}

function applyRefreshStream(stream, body) {
  const parsed = new DOMParser().parseFromString(stream, 'text/html');
  let applied = false;
  for (const update of parsed.querySelectorAll('turbo-stream')) {
    const action = update.getAttribute('action');
    if (!['replace', 'update'].includes(action)) continue;
    const template = update.querySelector('template');
    if (!template) continue;
    const id = update.getAttribute('target');
    const selector = update.getAttribute('targets');
    const targets = id
      ? Array.from(body.querySelectorAll('[id]')).filter(element => element.id === id)
      : selector ? body.querySelectorAll(selector) : [];
    for (const target of targets) {
      const content = template.content.cloneNode(true);
      if (action === 'replace') target.replaceWith(content);
      else target.replaceChildren(content);
      applied = true;
    }
  }
  return applied;
}
